import React from "react";

/**
 * Simple password-strength scorer + visual bar.
 * Score 0 (empty) → 5 (very strong). Heuristics:
 *   +1 per class present: lowercase, UPPERCASE, digit, symbol
 *   +1 if length ≥ 12
 *   -1 penalty if contains obvious keyboard walks / repeats (aaa, 123, qwerty)
 * Result clamped 0-5. Colour band + label shown.
 */

const KB_WALKS = ["qwerty", "azerty", "123456", "12345", "abcdef", "password", "admin", "letmein", "change@me1"];

export function scorePassword(pw) {
  if (!pw) return 0;
  let s = 0;
  if (/[a-z]/.test(pw)) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  if (pw.length >= 12) s++;
  if (pw.length < 8) s = Math.min(s, 1);
  const lower = pw.toLowerCase();
  if (KB_WALKS.some((w) => lower.includes(w))) s = Math.max(0, s - 2);
  if (/(.)\1{2,}/.test(pw)) s = Math.max(0, s - 1); // aaa, 111
  return Math.min(5, s);
}

const BANDS = [
  { label: "Too short", color: "bg-slate-200", text: "text-slate-500" },
  { label: "Weak", color: "bg-red-500", text: "text-red-700" },
  { label: "Fair", color: "bg-amber-500", text: "text-amber-700" },
  { label: "Good", color: "bg-lime-500", text: "text-lime-700" },
  { label: "Strong", color: "bg-emerald-500", text: "text-emerald-700" },
  { label: "Very strong", color: "bg-emerald-600", text: "text-emerald-800" },
];

export default function PasswordStrengthMeter({ password, testid = "pwd-strength" }) {
  const s = scorePassword(password || "");
  const band = BANDS[s];
  return (
    <div className="mt-1" data-testid={testid} data-strength={s}>
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-1.5 flex-1 rounded ${i < s ? band.color : "bg-slate-200"}`} />
        ))}
      </div>
      <div className={`mt-1 text-[11px] ${band.text}`}>
        {password ? band.label : "Enter a password"}
        {password && s < 3 && (
          <span className="ms-1 text-muted-foreground">
            — mix upper + lower + digit + symbol, use 12+ chars for very strong.
          </span>
        )}
      </div>
    </div>
  );
}
